/*
 * Copyright (c) 2025 Benjamin Siciliano
 * All rights reserved.
 * 
 * This file is part of the benjaminsiciliano.com project.
 * Unauthorized copying, modification, distribution, or use is strictly prohibited.
 * For licensing inquiries, contact: ben.siciliano@gmail.com
 */

import React, { useEffect, useRef, useState, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../contexts/ThemeContext';
import cacheManager from '../utils/cache.js';
import performanceMonitor from '../utils/performance.js';
import { applyDynamicSizing } from '../utils/centralityScoring.js';
import './NetworkVisualization.css';

const NetworkVisualization = forwardRef(({ dataFile = 'biotech', hideUI = false, onNodeClick, onEdgeClick, exploreMode = false, exploreState = null, onNodeToggle = null }, ref) => {
  const { theme, isThemeChanging } = useTheme();
  const svgRef = useRef();
  const containerRef = useRef();
  const simulationRef = useRef();
  const [selectedNode, setSelectedNode] = useState(null);
  const [connectedNodes, setConnectedNodes] = useState(new Set());
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [edgeConnectedNodes, setEdgeConnectedNodes] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const selectedNodeRef = useRef(null);
  const [lastCommitDate, setLastCommitDate] = useState('July 2025');
  const [networkData, setNetworkData] = useState({ nodes: [], links: [] });
  const [nodeTypeMap, setNodeTypeMap] = useState({});
  const [nodeColors, setNodeColors] = useState({});
  const [linkTypeMap, setLinkTypeMap] = useState({});
  const [linkColors, setLinkColors] = useState({});
  
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      let rawData;
      if (dataFile === 'tech') {
        const { atlantaTechEcosystem, nodeTypeMap: ntm, nodeColors: nc, linkTypeMap: ltm, linkColors: lc } = await import('../atlanta_tech_data.js');
        rawData = atlantaTechEcosystem;
        setNodeTypeMap(ntm);
        setNodeColors(nc);
        setLinkTypeMap(ltm || {});
        setLinkColors(lc || {});
      } else {
        const { atlantaBiotechEcosystem, nodeTypeMap: ntm, nodeColors: nc, linkTypeMap: ltm, linkColors: lc } = await import('../atlanta_biotech_data.js');
        rawData = atlantaBiotechEcosystem;
        setNodeTypeMap(ntm);
        setNodeColors(nc);
        setLinkTypeMap(ltm || {});
        setLinkColors(lc || {});
      }
      
      // Apply dynamic sizing by default
      const dynamicData = applyDynamicSizing(rawData, {
        minSize: 5,
        maxSize: 30,
        scalingMethod: 'logarithmic'
      });
      setNetworkData(dynamicData);
      
      console.log('Dynamic sizing applied');
      setIsLoading(false);
    };
    loadData();
  }, [dataFile]);
  
  // Function to fetch last git commit date
  const fetchLastCommitDate = useCallback(async () => {
    try {
      const response = await fetch('/last-commit-date.json');
      if (response.ok) {
        const data = await response.json();
        setLastCommitDate(data.lastCommitDate);
      }
    } catch (error) {
      console.log('Could not fetch last commit date:', error);
      // Keep default date if fetch fails
    }
  }, []);

  // Initialize caching and performance monitoring
  useEffect(() => {
    // Start performance monitoring
    performanceMonitor.start();
    
    // Warm cache on component start
    cacheManager.warmCache();
    
    // Cache user preferences
    const userPrefs = {
      theme: localStorage.getItem('theme') || 'dark',
      lastVisit: Date.now(),
      visitCount: parseInt(localStorage.getItem('visitCount') || '0') + 1
    };
    
    cacheManager.cacheUserPrefs(userPrefs);
    localStorage.setItem('visitCount', userPrefs.visitCount.toString());
    
    console.log('Caching system initialized in NetworkVisualization');
  }, []);

  // Fetch last commit date and cached network data on component mount
  useEffect(() => {
    fetchLastCommitDate();
    
    // Fetch cached network data (zoom, center, bounds)
    const fetchCachedNetworkData = async () => {
      // Check cache first
      const cached = cacheManager.get('assets', 'optimal-zoom');
      if (cached) {
        if (cached.optimalZoom) {
          setZoomLevel(cached.optimalZoom);
        }
        if (cached.networkCenter && cached.networkBounds) {
          window.cachedNetworkData = {
            center: cached.networkCenter,
            bounds: cached.networkBounds,
            viewport: cached.viewport
          };
        }
        return;
      }

      const startTime = performance.now();
      try {
        const response = await fetch('/optimal-zoom.json');
        if (response.ok) {
          const data = await response.json();
          const duration = performance.now() - startTime;
          
          performanceMonitor.trackNetworkInteraction('fetch-optimal-zoom', duration, true);
          cacheManager.set('assets', 'optimal-zoom', data);
          
          if (data.optimalZoom) {
            setZoomLevel(data.optimalZoom);
          }
          // Store cached network center and bounds for later use
          if (data.networkCenter && data.networkBounds) {
            window.cachedNetworkData = {
              center: data.networkCenter,
              bounds: data.networkBounds,
              viewport: data.viewport
            };
          }
        }
      } catch (error) {
        const duration = performance.now() - startTime;
        performanceMonitor.trackNetworkInteraction('fetch-optimal-zoom', duration, false);
        console.log('Could not fetch cached network data:', error);
        // Keep default zoom if fetch fails
      }
    };
    
    fetchCachedNetworkData();
  }, [fetchLastCommitDate]);
  
  // Sidebar state
  const [showControls, setShowControls] = useState(true); // Controls open by default
  const [showFilters, setShowFilters] = useState(false); // Collapsed by default
  const [showLegend, setShowLegend] = useState(false); // Collapsed by default
  const [showSearch, setShowSearch] = useState(false); // Collapsed by default
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  
  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    getNetworkData: () => ({ nodes: networkData.nodes, links: networkData.links, nodeTypeMap: typeMap }),
    setFilters,
    setSearchQuery,
    setSearchResults,
    manualCenterNetwork,
    centerNetwork,
    zoomIn,
    zoomOut,
    zoomToSubnetwork,
    getSearchResults: () => searchResults,
    setSelectedNode: (node) => {
      // Set the selected node and update connected nodes
      setSelectedNode(node);
      if (node) {
        // Find all connected nodes
        const connected = new Set();
        connected.add(node.id);
        networkData.links.forEach(link => {
          const linkSource = typeof link.source === 'string' ? link.source : link.source.id;
          const linkTarget = typeof link.target === 'string' ? link.target : link.target.id;
          if (linkSource === node.id) {
            connected.add(linkTarget);
          } else if (linkTarget === node.id) {
            connected.add(linkSource);
          }
        });
        setConnectedNodes(connected);
      } else {
        setConnectedNodes(new Set());
      }
    },
    setConnectedNodes: (connected) => {
      setConnectedNodes(connected);
    },
    highlightNode: (nodeId) => {
      // Highlight a specific node by ID
      console.log('highlightNode called with nodeId:', nodeId);
      if (svgRef.current) {
        const svg = d3.select(svgRef.current);
        console.log('SVG ref available, clearing previous highlights');
        // Remove previous highlights
        svg.selectAll('.node-group').classed('highlighted', false);
        console.log('Adding highlight to node:', nodeId);
        // Add highlight to specific node
        const targetNode = svg.selectAll('.node-group').filter(d => d.id === nodeId);
        console.log('Found target node elements:', targetNode.size());
        targetNode.classed('highlighted', true);
        console.log('Highlight applied');
      } else {
        console.log('SVG ref not available');
      }
    },
    highlightConnection: (sourceId, targetId) => {
      // Highlight a specific connection/edge
      if (svgRef.current) {
        const svg = d3.select(svgRef.current);
        // Remove previous connection highlights
        svg.selectAll('.link').classed('link-highlighted', false);
        // Add highlight to specific connection
        svg.selectAll('.link').filter(d => {
          const dSourceId = typeof d.source === 'object' ? d.source.id : d.source;
          const dTargetId = typeof d.target === 'object' ? d.target.id : d.target;
          return (dSourceId === sourceId && dTargetId === targetId) || 
                 (dSourceId === targetId && dTargetId === sourceId);
        }).classed('link-highlighted', true);
      }
    },
    setSelectedEdge: (sourceId, targetId) => {
      // Create a unique edge identifier (sorted to handle both directions)
      const edgeId = sourceId < targetId ? `${sourceId}-${targetId}` : `${targetId}-${sourceId}`;
      console.log('setSelectedEdge called with:', { sourceId, targetId, edgeId });
      console.log('Previous selectedEdgeId:', selectedEdgeId);
      setSelectedEdgeId(edgeId);
      console.log('setSelectedEdgeId called with:', edgeId);
      
      // Set the connected nodes for this edge
      const connected = new Set();
      connected.add(sourceId);
      connected.add(targetId);
      setEdgeConnectedNodes(connected);
    },
    clearHighlight: (clearEdgeSelection = true) => {
      // Clear all highlights
      if (svgRef.current) {
        const svg = d3.select(svgRef.current);
        svg.selectAll('.node-group').classed('highlighted', false);
        svg.selectAll('.node-group').classed('node-highlighted', false);
        svg.selectAll('.node-group').classed('node-dimmed', false);
        svg.selectAll('.label').classed('label-highlighted', false);
        svg.selectAll('.label').classed('label-dimmed', false);
        svg.selectAll('.link').classed('link-highlighted', false);
        svg.selectAll('.link').classed('link-dimmed', false);
      }
      // Only clear edge selection if explicitly requested
      if (clearEdgeSelection) {
        setSelectedEdgeId(null);
        setEdgeConnectedNodes(new Set());
      }
      // Always clear node selection
      setSelectedNode(null);
    },
    clearNodeSelection: () => {
      // Clear node selection state
      setSelectedNode(null);
      setConnectedNodes(new Set());
      
      // Also clear visual highlighting classes
      if (svgRef.current) {
        const svg = d3.select(svgRef.current);
        svg.selectAll('.node-group').classed('highlighted', false);
        svg.selectAll('.node-group').classed('node-highlighted', false);
        svg.selectAll('.node-group').classed('node-dimmed', false);
        svg.selectAll('.label').classed('label-highlighted', false);
        svg.selectAll('.label').classed('label-dimmed', false);
      }
    },
    getSelectedEdgeId: () => {
      console.log('getSelectedEdgeId called, returning:', selectedEdgeId);
      return selectedEdgeId;
    }
  }));
  
  // Detect mobile device
  const isMobile = useMemo(() => {
    const mobile = window.innerWidth <= 768;
    console.log('Mobile detection:', mobile, 'Window width:', window.innerWidth);
    return mobile;
  }, []);

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => {
      const newMobile = window.innerWidth <= 768;
      console.log('Window resized - new mobile state:', newMobile, 'Window width:', window.innerWidth);
      
      // Don't automatically change control states on resize
      // Let users manually expand the controls they need
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set mobile states when isMobile changes
  useEffect(() => {
    console.log('isMobile changed to:', isMobile);
    // Keep controls collapsed by default on both mobile and desktop
    // Users can manually expand the controls they need
    setShowFilters(false);
    setShowLegend(false);
    setShowSearch(false);
    // Note: showControls is now open by default, so we don't set it to false here
    console.log('Controls open by default on all screen sizes');
  }, [isMobile]);

  // Initialize zoom level from URL parameters or pre-calculated optimal zoom
  // Calculate optimal zoom and center for current filtered network
  const calculateOptimalZoomForCurrentNetwork = useCallback(() => {
    if (!containerRef.current) return { zoom: 0.17, center: { x: 0, y: 0 } };
    
    const svg = d3.select(containerRef.current).select("svg");
    const width = svg.node().getBoundingClientRect().width;
    const height = svg.node().getBoundingClientRect().height;
    
    // Get current filtered nodes from simulation
    const nodes = simulationRef.current ? simulationRef.current.nodes() : [];
    if (nodes.length === 0) return { zoom: 0.17, center: { x: 0, y: 0 } };
    
    // Calculate bounds including label radius for each node
    const bounds = nodes.map(n => {
      const labelPadding = isMobile ? 15 : 25;
      const nodeRadius = Math.max(n.size * (isMobile ? 2.5 : 3.5), 16) + 4;
      
      // Estimate label dimensions
      const fontSize = isMobile ? 20 : 28;
      const avgCharWidth = fontSize * 0.6;
      const estimatedLabelWidth = Math.max(n.name.length * avgCharWidth, 100);
      const estimatedLabelHeight = fontSize * 1.2;
      
      // Calculate label radius using same logic as collision detection
      const labelRadiusX = (estimatedLabelWidth / 2) + labelPadding;
      const labelRadiusY = (estimatedLabelHeight / 2) + labelPadding;
      const totalRadius = Math.max(labelRadiusX, labelRadiusY, nodeRadius + labelPadding);
      
      return {
        left: n.x - totalRadius,
        right: n.x + totalRadius,
        top: n.y - totalRadius,
        bottom: n.y + totalRadius
      };
    });
    
    // Find the overall bounds
    const minX = Math.min(...bounds.map(b => b.left));
    const maxX = Math.max(...bounds.map(b => b.right));
    const minY = Math.min(...bounds.map(b => b.top));
    const maxY = Math.max(...bounds.map(b => b.bottom));
    
    // Calculate the center of the network
    const networkCenterX = (minX + maxX) / 2;
    const networkCenterY = (minY + maxY) / 2;
    
    // Calculate the dimensions of the network
    const networkWidth = maxX - minX;
    const networkHeight = maxY - minY;
    
    // Add padding around the network to ensure all nodes are visible
    const padding = 25;
    
    // Calculate the scale to fit the network in the viewport
    const scaleX = (width - padding * 2) / networkWidth;
    const scaleY = (height - padding * 2) / networkHeight;
    const scale = Math.min(scaleX, scaleY, 1.0); // Cap at 100% zoom
    
    return {
      zoom: Math.max(scale, 0.1), // Minimum 10% zoom
      center: { x: networkCenterX, y: networkCenterY }
    };
  }, [isMobile]);

  const getInitialZoomLevel = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const zoomParam = urlParams.get('zoom');
    return zoomParam ? parseFloat(zoomParam) : 0.17; // Use pre-calculated optimal zoom as default
  };

  const [zoomLevel, setZoomLevel] = useState(getInitialZoomLevel);
  // Initialize filters from URL parameters or defaults
  const getInitialFilters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const filterParams = urlParams.getAll('filter');
    
    const defaultFilters = {
      companies: true,
      startups: true,  // Added startups filter
      universities: true,
      incubators: true,
      vcs: true,
      serviceProviders: true,
      healthSystems: true,
      government: true,
      trade: true,
      development: true,
      facilities: true,
      communityBuilders: true
    };
    
    // If URL has filter parameters, only enable those
    if (filterParams.length > 0) {
      const enabledFilters = {};
      Object.keys(defaultFilters).forEach(key => {
        enabledFilters[key] = filterParams.includes(key);
      });
      return enabledFilters;
    }
    
    return defaultFilters;
  };

  const [filters, setFilters] = useState(getInitialFilters);

  // Store zoom behavior reference
  const zoomBehaviorRef = useRef(null);

  // Helper function to get the selected edge object from the ID
  const getSelectedEdge = useMemo(() => {
    if (!selectedEdgeId) return null;
    return networkData.links.find(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      const linkId = sourceId < targetId ? `${sourceId}-${targetId}` : `${targetId}-${sourceId}`;
      return linkId === selectedEdgeId;
    });
  }, [selectedEdgeId, networkData.links]);

  // Use imported color scheme and type mapping
  const typeMap = nodeTypeMap;
  
  // Theme-aware color scheme
  const getNodeColor = (nodeType) => {
    const baseColor = nodeColors[typeMap[nodeType]];
    if (!baseColor) return theme === 'dark' ? '#ffffff' : '#000000';
    
    if (theme === 'light') {
      // Convert dark colors to lighter versions for better readability
      const colorMap = {
        '#0033A0': '#4A90E2', // Academia & Research / University - Lighter Blue
        '#0D6A42': '#4CAF50', // Company - Lighter Green
        '#F2A900': '#FFD54F', // Investor / Venture Capital - Lighter Gold
        '#E91E63': '#F48FB1', // Accelerator & Incubator - Lighter Magenta/Pink
        '#5A2D81': '#BA68C8', // Government & Trade Org - Lighter Purple
        '#545454': '#9E9E9E', // Service Provider - Lighter Gray
        '#00AEEF': '#81D4FA', // Startup - Lighter Blue
        '#2E7D32': '#66BB6A', // Provider & Health System / Facilities - Lighter Forest Green
        '#E67300': '#FFB74D', // Community Builders - Lighter Orange
      };
      return colorMap[baseColor] || baseColor;
    }
    
    return baseColor;
  };

  // Enhanced link styling based on centralized type mapping
  const getLinkStyle = (linkType) => {
    // Get the category for this link type
    const category = linkTypeMap[linkType] || 'Unknown';
    
    // Get the base color for this category
    const baseColor = linkColors[category] || '#a0a0a0';
    
    // Define stroke patterns and weights based on category
    const getCategoryStyle = (cat) => {
      switch (cat) {
        case 'Financial':
          return { strokeWidth: 6, strokeDasharray: '8,4', opacity: 0.5 };
        case 'Collaboration':
          return { strokeWidth: 6, strokeDasharray: '2,2', opacity: 0.4 };
        case 'Support':
          return { strokeWidth: 6, strokeDasharray: '6,3', opacity: 0.4 };
        case 'Organizational':
          return { strokeWidth: 6, strokeDasharray: 'none', opacity: 0.6 };
        case 'Infrastructure':
          return { strokeWidth: 4, strokeDasharray: 'none', opacity: 0.3 };
        case 'Pilot & Testing':
          return { strokeWidth: 6, strokeDasharray: '4,4', opacity: 0.5 };
        default:
          return { strokeWidth: 6, strokeDasharray: 'none', opacity: 0.2 };
      }
    };
    
    const categoryStyle = getCategoryStyle(category);
    
    return {
      stroke: baseColor,
      strokeWidth: categoryStyle.strokeWidth,
      strokeDasharray: categoryStyle.strokeDasharray,
      opacity: categoryStyle.opacity
    };
  };

  // Performance optimization: Debounced filter updates
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [filters]);

  // Create filter mapping
  const filterMapping = useMemo(() => ({
    'university': 'universities',
    'company': 'companies',
    'public_company': 'companies',
    'startup': 'startups',  // Changed from 'companies' to 'startups'
    'vc': 'vcs',
    'incubator': 'incubators',
    'accelerator': 'incubators',
    'accelerator_program': 'incubators',  // Added missing mapping
    'facility': 'facilities',
    'lab_facility': 'facilities',  // Added missing mapping
    'research_park': 'facilities',  // Added missing mapping
    'research_institution': 'universities',  // Added missing mapping
    'health_system': 'healthSystems',
    'serviceProvider': 'serviceProviders',
    'government': 'government',
    'trade': 'trade',
    'development': 'development',
    'community': 'communityBuilders'
  }), []);

  // Center network function with improved visibility
  const centerNetwork = useCallback(() => {
    if (svgRef.current && zoomBehaviorRef.current && simulationRef.current) {
      const svg = d3.select(svgRef.current);
      const width = svg.node().getBoundingClientRect().width;
      const height = svg.node().getBoundingClientRect().height;
      
      // Calculate optimal zoom and center for current filtered network
      const { zoom, center } = calculateOptimalZoomForCurrentNetwork();
      
      if (center.x !== 0 || center.y !== 0) {
        // Calculate the transform to center the network in the viewport
        const transform = d3.zoomIdentity
          .translate(width / 2 - center.x * zoom, height / 2 - center.y * zoom)
          .scale(zoom);
        
        // Apply the transform with a smooth transition
        svg.transition()
          .duration(500)
          .call(zoomBehaviorRef.current.transform, transform);
        
        // Update zoom level state
        setZoomLevel(zoom);
      } else {
        // Fallback to optimal zoom if no nodes
        const transform = d3.zoomIdentity
          .translate(width / 2, height / 2)
          .scale(0.17); // 17% optimal zoom fallback
        
        svg.transition()
          .duration(500)
          .call(zoomBehaviorRef.current.transform, transform);
        
        // Update zoom level state
        setZoomLevel(0.17);
      }
    }
  }, [calculateOptimalZoomForCurrentNetwork]);

  // Manual center network function (doesn't hide the plot)
  const manualCenterNetwork = useCallback(() => {
    if (svgRef.current && zoomBehaviorRef.current && simulationRef.current) {
      const svg = d3.select(svgRef.current);
      const width = svg.node().getBoundingClientRect().width;
      const height = svg.node().getBoundingClientRect().height;
      
      // Calculate optimal zoom and center for current filtered network
      const { zoom, center } = calculateOptimalZoomForCurrentNetwork();
      
      if (center.x !== 0 || center.y !== 0) {
        // Calculate the transform to center the network in the viewport
        const transform = d3.zoomIdentity
          .translate(width / 2 - center.x * zoom, height / 2 - center.y * zoom)
          .scale(zoom);
        
        // Apply the transform with a smooth transition
        svg.transition()
          .duration(500)
          .call(zoomBehaviorRef.current.transform, transform);
        
        // Update zoom level state
        setZoomLevel(zoom);
      } else {
        // Fallback to optimal zoom if no nodes
        const transform = d3.zoomIdentity
          .translate(width / 2, height / 2)
          .scale(0.17); // 17% optimal zoom fallback
        
        svg.transition()
          .duration(500)
          .call(zoomBehaviorRef.current.transform, transform);
        
        // Update zoom level state
        setZoomLevel(0.17);
      }
    }
  }, [calculateOptimalZoomForCurrentNetwork]);

  // Zoom functions
  const zoomIn = useCallback(() => {
    const startTime = performance.now();
    
    if (svgRef.current && zoomBehaviorRef.current && zoomLevel < 1.0) {
      const newZoomLevel = Math.min(zoomLevel + 0.1, 1.0); // 10% step, max 100%
      setZoomLevel(newZoomLevel);
      
      // Cache zoom level
      cacheManager.setSession('zoom', 'level', newZoomLevel);
      
      d3.select(svgRef.current)
        .transition()
        .duration(300)
        .call(zoomBehaviorRef.current.scaleTo, newZoomLevel);
      
      const duration = performance.now() - startTime;
      performanceMonitor.trackUserInteraction('zoom-in', duration);
    }
  }, [zoomLevel]);

  const zoomOut = useCallback(() => {
    const startTime = performance.now();
    
    if (svgRef.current && zoomBehaviorRef.current && zoomLevel > 0.1) {
      const newZoomLevel = Math.max(zoomLevel - 0.1, 0.1); // 10% step, minimum 10%
      setZoomLevel(newZoomLevel);
      
      // Cache zoom level
      cacheManager.setSession('zoom', 'level', newZoomLevel);
      
      d3.select(svgRef.current)
        .transition()
        .duration(300)
        .call(zoomBehaviorRef.current.scaleTo, newZoomLevel);
      
      const duration = performance.now() - startTime;
      performanceMonitor.trackUserInteraction('zoom-out', duration);
    }
  }, [zoomLevel]);

  // Helper function to calculate base zoom level using outermost node/label plus padding method
  const calculateBaseZoomLevel = useCallback(() => {
    if (!svgRef.current || !simulationRef.current) return 0.17; // Fallback to optimal zoom
    
    const svg = d3.select(svgRef.current);
    const width = svg.node().getBoundingClientRect().width;
    const height = svg.node().getBoundingClientRect().height;
    
    // Get the simulation data for all visible nodes
    const nodes = simulationRef.current.nodes();
    if (nodes.length === 0) return 0.17; // Fallback to optimal zoom
    
    // Calculate bounds including label radius for each node (same method as zoomToSubnetwork)
    const bounds = nodes.map(n => {
      const labelPadding = isMobile ? 15 : 25;
      const nodeRadius = Math.max(n.size * (isMobile ? 2.5 : 3.5), 16) + 4; // Add glow padding
      
      // Estimate label dimensions
      const fontSize = isMobile ? 20 : 28;
      const avgCharWidth = fontSize * 0.6;
      const estimatedLabelWidth = Math.max(n.name.length * avgCharWidth, 100);
      const estimatedLabelHeight = fontSize * 1.2;
      
      // Calculate label radius using same logic as collision detection
      const labelRadiusX = (estimatedLabelWidth / 2) + labelPadding;
      const labelRadiusY = (estimatedLabelHeight / 2) + labelPadding;
      const totalRadius = Math.max(labelRadiusX, labelRadiusY, nodeRadius + labelPadding);
      
      return {
        left: n.x - totalRadius,
        right: n.x + totalRadius,
        top: n.y - totalRadius,
        bottom: n.y + totalRadius
      };
    });
    
    // Find the overall bounds
    const minX = Math.min(...bounds.map(b => b.left));
    const maxX = Math.max(...bounds.map(b => b.right));
    const minY = Math.min(...bounds.map(b => b.top));
    const maxY = Math.max(...bounds.map(b => b.bottom));
    
    // Calculate the dimensions of the network
    const networkWidth = maxX - minX;
    const networkHeight = maxY - minY;
    
            // Add padding around the network to ensure all nodes are visible
        const padding = 25; // Increased padding to ensure all nodes are visible
        
        // Calculate the scale to fit the network in the viewport
        const scaleX = (width - padding * 2) / networkWidth;
        const scaleY = (height - padding * 2) / networkHeight;
        const scale = Math.min(scaleX, scaleY);
    
    return scale;
  }, [isMobile]);

  // Zoom to subnetwork function
  const zoomToSubnetwork = useCallback((node) => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    
    const svg = d3.select(svgRef.current);
    const width = svg.node().getBoundingClientRect().width;
    const height = svg.node().getBoundingClientRect().height;
    
    // Find all connected nodes (the selected node + its immediate connections)
    const connectedNodeIds = new Set([node.id]);
    
    // Special handling for edge connections
    if (node.id === 'edge-center') {
      // For edge connections, only include the two nodes directly connected by this edge
      const sourceId = node.sourceId;
      const targetId = node.targetId;
      
      if (sourceId && targetId) {
        connectedNodeIds.add(sourceId);
        connectedNodeIds.add(targetId);
        // Don't add other connected nodes - just focus on the two nodes of this edge
      }
    } else {
      // Normal node selection
      networkData.links.forEach(link => {
        // Handle both string IDs and node objects in links
        const linkSource = typeof link.source === 'string' ? link.source : link.source.id;
        const linkTarget = typeof link.target === 'string' ? link.target : link.target.id;
        
        if (linkSource === node.id || linkTarget === node.id) {
          connectedNodeIds.add(linkSource);
          connectedNodeIds.add(linkTarget);
        }
      });
    }
    
    // Get the simulation data for connected nodes
    const connectedNodes = simulationRef.current.nodes().filter(d => connectedNodeIds.has(d.id));
    
    if (connectedNodes.length === 0) return;
    
    // Calculate bounds including label radius for each node (using same logic as collision detection)
    const bounds = connectedNodes.map(n => {
      // Use the same label radius calculation as collision detection, but with reduced padding for zoom
      const labelPadding = isMobile ? 15 : 25; // Reduced padding for zoom calculation
      const nodeRadius = Math.max(n.size * (isMobile ? 2.5 : 3.5), 16) + 4; // Add glow padding
      
      // Estimate label dimensions
      const fontSize = isMobile ? 20 : 28;
      const avgCharWidth = fontSize * 0.6;
      const estimatedLabelWidth = Math.max(n.name.length * avgCharWidth, 100);
      const estimatedLabelHeight = fontSize * 1.2;
      
      // Calculate label radius using same logic as collision detection
      const labelRadiusX = (estimatedLabelWidth / 2) + labelPadding;
      const labelRadiusY = (estimatedLabelHeight / 2) + labelPadding;
      const totalRadius = Math.max(labelRadiusX, labelRadiusY, nodeRadius + labelPadding);
      
      return {
        left: n.x - totalRadius,
        right: n.x + totalRadius,
        top: n.y - totalRadius,
        bottom: n.y + totalRadius
      };
    });
    
    // Find the overall bounds
    const minX = Math.min(...bounds.map(b => b.left));
    const maxX = Math.max(...bounds.map(b => b.right));
    const minY = Math.min(...bounds.map(b => b.top));
    const maxY = Math.max(...bounds.map(b => b.bottom));
    
    // Calculate the center of the subnetwork
    const subnetworkCenterX = (minX + maxX) / 2;
    const subnetworkCenterY = (minY + maxY) / 2;
    
    // Calculate the dimensions of the subnetwork
    const subnetworkWidth = maxX - minX;
    const subnetworkHeight = maxY - minY;
    
    // Add padding around the subnetwork to ensure all nodes are visible
    const padding = 25; // Increased padding to ensure all nodes are visible
    
    // Calculate the scale to fit the subnetwork in the viewport
    const scaleX = (width - padding * 2) / subnetworkWidth;
    const scaleY = (height - padding * 2) / subnetworkHeight;
    let scale = Math.min(scaleX, scaleY);
    
    // Apply zoom bounds using dynamic base zoom level
    const baseZoomLevel = calculateBaseZoomLevel();
    const minScale = baseZoomLevel; // Minimum zoom (same as center/reset zoom)
    const maxScale = baseZoomLevel * 2; // Maximum zoom (2x center/reset zoom) for large subnetworks
    scale = Math.max(scale, minScale);
    scale = Math.min(scale, maxScale);
    
    // Get current transform to check if panning is needed
    const currentTransform = d3.zoomTransform(svg.node());
    const currentScale = currentTransform.k;
    const currentTranslateX = currentTransform.x;
    const currentTranslateY = currentTransform.y;
    
    // Calculate the current viewport bounds in data coordinates
    const currentViewportLeft = -currentTranslateX / currentScale;
    const currentViewportRight = (width - currentTranslateX) / currentScale;
    const currentViewportTop = -currentTranslateY / currentScale;
    const currentViewportBottom = (height - currentTranslateY) / currentScale;
    
    // Check if the subnetwork is already fully visible in the current viewport
    const subnetworkFullyVisible = 
      minX >= currentViewportLeft && 
      maxX <= currentViewportRight && 
      minY >= currentViewportTop && 
      maxY <= currentViewportBottom;
    
    // Check if the scale is already appropriate (within 10% tolerance)
    const scaleDifference = Math.abs(currentScale - scale) / scale;
    const scaleAppropriate = scaleDifference < 0.1;
    
    // Only pan and zoom if the subnetwork is not fully visible or the scale is not appropriate
    if (!subnetworkFullyVisible || !scaleAppropriate) {
      // Debug logging for subnetworks
      if (connectedNodes.length > 5) {
        console.log(`Zooming to subnetwork for ${node.name}:`, {
          nodeCount: connectedNodes.length,
          subnetworkWidth: Math.round(subnetworkWidth),
          subnetworkHeight: Math.round(subnetworkHeight),
          viewportWidth: width,
          viewportHeight: height,
          padding,
          labelPadding: isMobile ? 15 : 25,
          bounds: bounds.length,
          baseZoomLevel,
          minScale,
          maxScale,
          finalScale: scale,
          originalScaleX: scaleX,
          originalScaleY: scaleY,
          currentScale,
          scaleDifference,
          subnetworkFullyVisible,
          scaleAppropriate,
          sampleBounds: bounds.slice(0, 3).map(b => ({
            left: Math.round(b.left),
            right: Math.round(b.right),
            top: Math.round(b.top),
            bottom: Math.round(b.bottom)
          }))
        });
      }
      
      // Calculate the transform to center the subnetwork in the viewport
      const transform = d3.zoomIdentity
        .translate(width / 2 - subnetworkCenterX * scale, height / 2 - subnetworkCenterY * scale)
        .scale(scale);
      
      // Apply the transform with a smooth transition
      svg.transition()
        .duration(800)
        .call(zoomBehaviorRef.current.transform, transform);
      
      // Update zoom level state
      setZoomLevel(scale);
    } else {
      // Debug logging when no panning is needed
      if (connectedNodes.length > 5) {
        console.log(`Subnetwork for ${node.name} already visible, no panning needed:`, {
          nodeCount: connectedNodes.length,
          subnetworkFullyVisible,
          scaleAppropriate,
          currentScale,
          targetScale: scale,
          scaleDifference
        });
      }
    }
  }, [networkData.links, calculateBaseZoomLevel, isMobile]);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    setIsLoading(true);
    
    // Show SVG immediately instead of hiding it
    const svg = d3.select(svgRef.current);
    svg.style("opacity", "1");
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Clear previous content
    svg.selectAll("*").remove();

    // Update SVG dimensions to match container
    svg.attr("width", width).attr("height", height);

    // Use all nodes initially - filtering will be handled by visibility
    const filteredNodes = networkData.nodes;

    // Use all links initially - filtering will be handled by visibility
    const processedLinks = networkData.links.map(link => {
      const sourceNode = filteredNodes.find(n => n.id === link.source);
      const targetNode = filteredNodes.find(n => n.id === link.target);
      return {
        ...link,
        source: sourceNode,
        target: targetNode
      };
    }).filter(link => link.source && link.target);

    // Calculate cluster positions for better organization
    const clusterPositions = {};
    const nodeTypes = [...new Set(filteredNodes.map(n => n.type))];
    const clustersPerRow = Math.ceil(Math.sqrt(nodeTypes.length));
    
    // Use more spread out clustering in explore mode to reduce overlap
    const clusterSpacing = exploreMode ? 0.8 : 1.0; // Reduce cluster density in explore mode
    
    nodeTypes.forEach((type, index) => {
      const row = Math.floor(index / clustersPerRow);
      const col = index % clustersPerRow;
      clusterPositions[type] = {
        x: (col + 0.5) * (width / clustersPerRow) * clusterSpacing + (width * (1 - clusterSpacing) / 2),
        y: (row + 0.5) * (height / Math.ceil(nodeTypes.length / clustersPerRow)) * clusterSpacing + (height * (1 - clusterSpacing) / 2)
      };
    });

    // Create force simulation with faster settings
    // Use more conservative spacing in explore mode to reduce label overlap
    const baseLinkDistance = isMobile ? 200 : 280;
    const baseChargeStrength = isMobile ? -300 : -500;
    const baseCollisionRadius = (d) => Math.max(d.size * (isMobile ? 2.5 : 3.5), 16) + 4 + (isMobile ? 30 : 50);
    
    // Adjust parameters for explore mode to reduce overlap
    const linkDistance = exploreMode ? baseLinkDistance * 1.4 : baseLinkDistance;
    const chargeStrength = exploreMode ? baseChargeStrength * 1.3 : baseChargeStrength;
    const collisionRadius = exploreMode ? (d) => baseCollisionRadius(d) * 1.5 : baseCollisionRadius;
    
    const simulation = d3.forceSimulation(filteredNodes)
      .force("link", d3.forceLink(processedLinks).id(d => d.id).distance(linkDistance))
      .force("charge", d3.forceManyBody().strength(chargeStrength))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius(collisionRadius))
      .force("x", d3.forceX(d => clusterPositions[d.type]?.x || width / 2).strength(0.1))
      .force("y", d3.forceY(d => clusterPositions[d.type]?.y || height / 2).strength(0.1))
      .alphaDecay(0.1) // Faster decay (default is 0.0228)
      .velocityDecay(0.3); // Faster velocity decay
    
    // Store simulation reference
    simulationRef.current = simulation;

    // Create a zoom group that contains all the network elements
    const zoomGroup = svg.append("g").attr("class", "zoom-group");

    // Apply initial zoom and centering after simulation has positioned nodes
    // We'll set up the zoom behavior first, then apply the optimal transform
    const zoom = d3.zoom()
      .scaleExtent([0.1, 1.0]) // Zoom range from 10% to 100%
      .wheelDelta(event => -event.deltaY * (event.deltaMode ? 120 : 1) / 100) // Faster wheel zoom for desktop
      .on("zoom", (event) => {
        // Direct DOM update for faster response on desktop
        zoomGroup.attr("transform", event.transform);
        setZoomLevel(event.transform.k);
      })
      .on("start", (event) => {
        // Prevent default only for non-touch events to allow native iOS gestures
        if (!event.sourceEvent || event.sourceEvent.type !== 'touchstart') {
          event.sourceEvent?.preventDefault();
        }
      });

    // Store reference to zoom behavior
    zoomBehaviorRef.current = zoom;

    // Enable zoom for both desktop and mobile, but with different configurations
    if (!isMobile) {
      // Desktop: full zoom functionality
      svg.call(zoom);
    } else {
      // Mobile: enable zoom with iOS Safari compatibility
      svg.call(zoom);
      
      // Add passive touch listeners to improve iOS Safari performance
      svg.on("touchstart", function(event) {
        // Don't prevent default - let native gestures work
        // D3 will handle the zoom behavior
      }, { passive: true });
      
      svg.on("touchmove", function(event) {
        // Don't prevent default - let native gestures work
        // D3 will handle the zoom behavior
      }, { passive: true });
      
      svg.on("touchend", function(event) {
        // Don't prevent default - let native gestures work
      }, { passive: true });
    }

    // Create links in zoom group
    const links = zoomGroup.append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(processedLinks)
      .enter().append("line")
      .attr("stroke", d => getLinkStyle(d.type).stroke)
      .attr("stroke-width", d => getLinkStyle(d.type).strokeWidth)
      .attr("stroke-dasharray", d => getLinkStyle(d.type).strokeDasharray)
      .attr("opacity", d => getLinkStyle(d.type).opacity)
      .style("pointer-events", "auto")
      .style("cursor", "pointer")
      .attr("class", d => {
        const createEdgeId = (edge) => {
          const sourceId = edge.source.id;
          const targetId = edge.target.id;
          return sourceId < targetId ? `${sourceId}-${targetId}` : `${targetId}-${sourceId}`;
        };
        const currentEdgeId = createEdgeId(d);
        const isHighlighted = currentEdgeId === selectedEdgeId;
        if (isHighlighted) {
          console.log('Highlighting edge:', d.source.id, '->', d.target.id, 'with ID:', currentEdgeId);
        }
        
        let classes = 'link';
        if (isHighlighted) {
          classes += ' link-highlighted';
        }
        
        if (exploreMode && exploreState) {
          const sourceSelected = exploreState.selectedNodes.has(d.source.id);
          const targetSelected = exploreState.selectedNodes.has(d.target.id);
          
          // Show link only if both nodes are selected (saved)
          if (sourceSelected && targetSelected) {
            classes += ' link-selected';
          } else {
            classes += ' link-hidden';
          }
        }
        
        return classes;
      })
      .on("click", function(event, d) {
        event.stopPropagation(); // Prevent bubbling to container
        console.log('D3 link click event:', d.source.id, '->', d.target.id);
        handleEdgeClick(d, event);
      });

    // Create nodes in zoom group with mobile-optimized sizes
    const nodes = zoomGroup.append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(filteredNodes)
      .enter().append("g")
      .attr("class", d => {
        let classes = 'node-group';
        if (selectedNode && selectedNode.id === d.id) {
          classes += ' node-highlighted';
        }
        if (exploreMode && exploreState) {
          if (exploreState.selectedNodes.has(d.id)) {
            classes += ' node-selected';
          } else if (exploreState.visibleNodes.has(d.id)) {
            classes += ' node-visible';
          } else {
            classes += ' node-hidden';
          }
        }
        return classes;
      })
      .on("click", function(event, d) {
        event.stopPropagation(); // Prevent bubbling to container
        console.log('D3 node click event:', d.id);
        if (exploreMode && onNodeToggle) {
          onNodeToggle(d.id, event);
        } else {
          handleNodeClick(d, event);
        }
      });

    // Add background glow circle
    nodes.append("circle")
      .attr("class", "node-glow")
      .attr("r", d => Math.max(d.size * (isMobile ? 2.5 : 3.5), 16) + 4)
      .attr("fill", "rgba(255, 255, 255, 0.1)")
      .style("pointer-events", "none");

    // Add main node circle
    nodes.append("circle")
      .attr("class", "node-main")
      .attr("r", d => Math.max(d.size * (isMobile ? 2.5 : 3.5), 16))
      .attr("fill", d => getNodeColor(d.type))
      .style("cursor", "pointer")
      .style("filter", isMobile ? "none" : (theme === 'dark' ? "drop-shadow(0 0 8px rgba(255,255,255,0.5))" : "drop-shadow(0 0 6px rgba(0,0,0,0.3))")) // Remove shadows on mobile
      .on("mouseover", function(event, d) {
        // Add hover class to the node group
        const nodeGroup = d3.select(this.parentNode);
        nodeGroup.classed("node-hovered", true);
        
        // Add hover class to the corresponding label
        const label = svg.selectAll(".label").filter(labelD => labelD.id === d.id);
        label.classed("label-hovered", true);
      })
      .on("mouseout", function(event, d) {
        // Remove hover class from the node group
        const nodeGroup = d3.select(this.parentNode);
        nodeGroup.classed("node-hovered", false);
        
        // Remove hover class from the corresponding label
        const label = svg.selectAll(".label").filter(labelD => labelD.id === d.id);
        label.classed("label-hovered", false);
      });




    // Create labels in zoom group with enhanced readability
    const labels = zoomGroup.append("g")
      .attr("class", "labels")
      .selectAll("text")
      .data(filteredNodes)
      .enter().append("text")
      .text(d => d.name)
      .attr("x", 0)
      .attr("y", 0)
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .attr("fill", theme === 'dark' ? "#fff" : "#1a1a1a") // Darker text for light theme
      .attr("font-size", isMobile ? "20px" : "28px") // Smaller font on mobile
      .attr("font-weight", "800") // Bolder font weight
      .style("pointer-events", "auto")
      .style("cursor", "pointer")
      .style("text-shadow", isMobile ? "none" : (theme === 'dark' ? 
        "1px 1px 2px rgba(0,0,0,0.6)" : 
        "1px 1px 1px rgba(255,255,255,0.6)")) // Reduced text shadows for better performance
      .style("font-family", "system-ui, -apple-system, sans-serif")
      .style("stroke", theme === 'dark' ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.4)") // Text outline
      .style("stroke-width", "0.3px") // Thinner outline for better contrast
      .attr("class", d => {
        let classes = 'label';
        if (selectedNode && selectedNode.id === d.id) {
          classes += ' label-highlighted';
        }
        if (exploreMode && exploreState) {
          if (exploreState.selectedNodes.has(d.id)) {
            classes += ' label-selected';
          } else if (exploreState.visibleNodes.has(d.id)) {
            classes += ' label-visible';
          } else {
            classes += ' label-hidden';
          }
        }
        return classes;
      })
      .on("click", function(event, d) {
        event.stopPropagation(); // Prevent bubbling to container
        console.log('D3 label click event:', d.id);
        if (exploreMode && onNodeToggle) {
          onNodeToggle(d.id, event);
        } else {
          handleNodeClick(d, event);
        }
      })
      .on("mouseover", function(event, d) {
        // Add hover class to the label
        d3.select(this).classed("label-hovered", true);
        
        // Add hover class to the corresponding node group
        const nodeGroup = svg.selectAll(".node-group").filter(nodeD => nodeD.id === d.id);
        nodeGroup.classed("node-hovered", true);
      })
      .on("mouseout", function(event, d) {
        // Remove hover class from the label
        d3.select(this).classed("label-hovered", false);
        
        // Remove hover class from the corresponding node group
        const nodeGroup = svg.selectAll(".node-group").filter(nodeD => nodeD.id === d.id);
        nodeGroup.classed("node-hovered", false);
      });

    // Update collision detection with actual label measurements after labels are rendered
    setTimeout(() => {
      // Create a more sophisticated collision detection that accounts for label boundaries
      simulation.force("collision", d3.forceCollide().radius(d => {
        const nodeRadius = Math.max(d.size * (isMobile ? 2.5 : 3.5), 16) + 4; // Add extra padding for glow
        const labelPadding = isMobile ? 30 : 50;
        
        // Find the corresponding label element to measure its actual bounds
        const labelElement = labels.filter(labelD => labelD.id === d.id).node();
        let labelRadius = nodeRadius + labelPadding;
        
        if (labelElement) {
          try {
            // Get the actual bounding box of the label
            const bbox = labelElement.getBBox();
            const labelWidth = bbox.width;
            const labelHeight = bbox.height;
            
            // Calculate the radius needed to encompass the label
            // Since labels are centered on nodes, we need half the label dimensions plus padding
            const labelRadiusX = (labelWidth / 2) + labelPadding;
            const labelRadiusY = (labelHeight / 2) + labelPadding;
            labelRadius = Math.max(labelRadiusX, labelRadiusY, nodeRadius + labelPadding);
            
            // Debug logging for a few nodes to verify the calculation
            if (d.id === filteredNodes[0]?.id || d.id === filteredNodes[Math.floor(filteredNodes.length / 2)]?.id) {
              console.log(`Node ${d.name}:`, {
                nodeRadius,
                labelWidth,
                labelHeight,
                labelRadiusX,
                labelRadiusY,
                finalRadius: labelRadius
              });
            }
          } catch (e) {
            // Fallback to estimated dimensions if getBBox fails
            const fontSize = isMobile ? 20 : 28;
            const avgCharWidth = fontSize * 0.6;
            const labelWidth = Math.max(d.name.length * avgCharWidth, 100);
            const labelHeight = fontSize * 1.2;
            const estimatedLabelRadius = Math.max(labelWidth / 2, labelHeight / 2) + labelPadding;
            labelRadius = Math.max(estimatedLabelRadius, nodeRadius + labelPadding);
            
            console.log(`Node ${d.name} (fallback):`, {
              nodeRadius,
              estimatedLabelWidth: labelWidth,
              estimatedLabelHeight: labelHeight,
              estimatedLabelRadius,
              finalRadius: labelRadius
            });
          }
        }
        

        
        return labelRadius;
      }));
      
      // Restart simulation with updated collision detection
      simulation.alpha(0.3).restart();
    }, 100); // Small delay to ensure labels are rendered



    // Update positions on simulation tick with performance optimization
    simulation.on("tick", () => {
      // Direct DOM updates for better performance
      links
        .attr("x1", d => d.source.x)
        .attr("y1", d => d.source.y)
        .attr("x2", d => d.target.x)
        .attr("y2", d => d.target.y);

      nodes
        .attr("transform", d => `translate(${d.x}, ${d.y})`);

      labels
        .attr("x", d => d.x)
        .attr("y", d => d.y);
    });

    // Apply optimal zoom and centering after simulation completes
    simulation.on("end", () => {
      // Use cached network center if available, otherwise calculate dynamically
      // eslint-disable-next-line no-unused-vars
      let networkCenterX, networkCenterY;
      
      // Calculate optimal zoom and center for current filtered network
      const { zoom, center } = calculateOptimalZoomForCurrentNetwork();
      
      if (center.x !== 0 || center.y !== 0) {
        // Apply the optimal transform with proper centering
        const optimalTransform = d3.zoomIdentity
          .translate(width / 2 - center.x * zoom, height / 2 - center.y * zoom)
          .scale(zoom);
        
        // Apply the transform immediately without transition to avoid 100% flash
        svg.call(zoomBehaviorRef.current.transform, optimalTransform);
        
        // Update zoom level state
        setZoomLevel(zoom);
      }
      
      // Mark as loaded immediately
      setIsLoading(false);
      
      // Re-center after a short delay to ensure simulation is fully settled
      setTimeout(() => {
        if (svgRef.current && zoomBehaviorRef.current && simulationRef.current) {
          const svg = d3.select(svgRef.current);
          const width = svg.node().getBoundingClientRect().width;
          const height = svg.node().getBoundingClientRect().height;
          
          // Recalculate optimal zoom and center for settled positions
          const { zoom: settledZoom, center: settledCenter } = calculateOptimalZoomForCurrentNetwork();
          
          if (settledCenter.x !== 0 || settledCenter.y !== 0) {
            // Only re-center if the position has changed significantly
            const currentTransform = d3.zoomTransform(svg.node());
            const currentCenterX = (width / 2 - currentTransform.x) / currentTransform.k;
            const currentCenterY = (height / 2 - currentTransform.y) / currentTransform.k;
            
            const centerDiffX = Math.abs(settledCenter.x - currentCenterX);
            const centerDiffY = Math.abs(settledCenter.y - currentCenterY);
            
            if (centerDiffX > 10 || centerDiffY > 10) {
              console.log('Re-centering after simulation settled:', {
                settledCenter: { x: settledCenter.x, y: settledCenter.y },
                currentCenter: { x: currentCenterX, y: currentCenterY },
                diff: { x: centerDiffX, y: centerDiffY },
                settledZoom
              });
              
              const settledTransform = d3.zoomIdentity
                .translate(width / 2 - settledCenter.x * settledZoom, height / 2 - settledCenter.y * settledZoom)
                .scale(settledZoom);
              
              svg.transition()
                .duration(300)
                .call(zoomBehaviorRef.current.transform, settledTransform);
              
              // Update zoom level state
              setZoomLevel(settledZoom);
            }
          }
        }
      }, 100);
    });

    return () => {
      simulation.stop();
      // Clean up D3 selections to prevent memory leaks
      svg.selectAll("*").remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkData, typeMap, theme, filterMapping, isMobile, calculateOptimalZoomForCurrentNetwork, exploreMode]);

  // Dynamic filtering - toggle node visibility without regenerating network
  useEffect(() => {
    if (!containerRef.current) return;
    
    const svg = d3.select(containerRef.current).select("svg");
    if (svg.empty()) return;
    
    // Update node visibility based on current filters
    svg.selectAll(".node-group")
      .each(function(d) {
        const nodeGroup = d3.select(this);
        const filterKey = filterMapping[d.type];
        const shouldShow = filterKey ? debouncedFilters[filterKey] : true;
        
        if (shouldShow) {
          nodeGroup.style("opacity", 1).style("display", "block");
        } else {
          nodeGroup.style("opacity", 0).style("display", "none");
        }
      });
    
    // Update label visibility based on current filters
    svg.selectAll(".label")
      .each(function(d) {
        const label = d3.select(this);
        const filterKey = filterMapping[d.type];
        const shouldShow = filterKey ? debouncedFilters[filterKey] : true;
        
        if (shouldShow) {
          label.style("opacity", 1).style("display", "block");
        } else {
          label.style("opacity", 0).style("display", "none");
        }
      });
    
    // Update link visibility based on current filters
    svg.selectAll(".link")
      .each(function(d) {
        const link = d3.select(this);
        const sourceFilterKey = filterMapping[d.source.type];
        const targetFilterKey = filterMapping[d.target.type];
        const sourceShouldShow = sourceFilterKey ? debouncedFilters[sourceFilterKey] : true;
        const targetShouldShow = targetFilterKey ? debouncedFilters[targetFilterKey] : true;
        const shouldShow = sourceShouldShow && targetShouldShow;
        
        if (shouldShow) {
          link.style("opacity", 1).style("display", "block");
        } else {
          link.style("opacity", 0).style("display", "none");
        }
      });
  }, [debouncedFilters, filterMapping]);

  // Update explore mode visibility without re-rendering the entire network
  useEffect(() => {
    if (!exploreMode || !exploreState || !containerRef.current) return;
    
    const svg = d3.select(containerRef.current).select("svg");
    if (svg.empty()) return;
    
    // Update node visibility classes
    svg.selectAll(".node-group")
      .classed("node-hidden", false)
      .classed("node-visible", false)
      .classed("node-selected", false)
      .each(function(d) {
        const nodeGroup = d3.select(this);
        if (exploreState.selectedNodes.has(d.id)) {
          nodeGroup.classed("node-selected", true);
        } else if (exploreState.visibleNodes.has(d.id)) {
          nodeGroup.classed("node-visible", true);
        } else {
          nodeGroup.classed("node-hidden", true);
        }
      });
    
    // Update label visibility classes
    svg.selectAll(".label")
      .classed("label-hidden", false)
      .classed("label-visible", false)
      .classed("label-selected", false)
      .each(function(d) {
        const label = d3.select(this);
        if (exploreState.selectedNodes.has(d.id)) {
          label.classed("label-selected", true);
        } else if (exploreState.visibleNodes.has(d.id)) {
          label.classed("label-visible", true);
        } else {
          label.classed("label-hidden", true);
        }
      });
    
    // Update link visibility classes
    svg.selectAll(".link")
      .classed("link-hidden", false)
      .classed("link-visible", false)
      .classed("link-selected", false)
      .each(function(d) {
        const link = d3.select(this);
        const sourceSelected = exploreState.selectedNodes.has(d.source.id);
        const targetSelected = exploreState.selectedNodes.has(d.target.id);
        
        if (sourceSelected && targetSelected) {
          link.classed("link-selected", true);
        } else {
          link.classed("link-hidden", true);
        }
      });
  }, [exploreMode, exploreState]);

  // Update node and edge highlighting when selections change
  useEffect(() => {
    if (!isLoading && containerRef.current) {
      const svg = d3.select(containerRef.current).select("svg");
      const nodeGroups = svg.selectAll(".node-group");
      const labels = svg.selectAll(".label");
      const links = svg.selectAll(".links line");
      
      // Remove all highlighting and dimming first
      nodeGroups.classed("node-highlighted", false);
      nodeGroups.classed("node-dimmed", false);
      labels.classed("label-highlighted", false);
      labels.classed("label-dimmed", false);
      links.classed("link-highlighted", false);
      links.classed("link-dimmed", false);
      
      if (selectedNode) {
        // Highlight only the selected node
        nodeGroups.filter(d => d.id === selectedNode.id).classed("node-highlighted", true);
        labels.filter(d => d.id === selectedNode.id).classed("label-highlighted", true);
        
        // Dim nodes that are not connected to the selected node
        nodeGroups.filter(d => !connectedNodes.has(d.id)).classed("node-dimmed", true);
        labels.filter(d => !connectedNodes.has(d.id)).classed("label-dimmed", true);
        links.filter(d => d.source.id !== selectedNode.id && d.target.id !== selectedNode.id)
          .classed("link-dimmed", true);
      } else if (getSelectedEdge) {
        // Highlight the selected edge
        links.filter(d => 
          (d.source.id === getSelectedEdge.source && d.target.id === getSelectedEdge.target) ||
          (d.source.id === getSelectedEdge.target && d.target.id === getSelectedEdge.source)
        ).classed("link-highlighted", true);
        
        // Highlight the nodes connected by this edge
        nodeGroups.filter(d => edgeConnectedNodes.has(d.id)).classed("node-highlighted", true);
        labels.filter(d => edgeConnectedNodes.has(d.id)).classed("label-highlighted", true);
        
        // Dim nodes that are not connected by this edge
        nodeGroups.filter(d => !edgeConnectedNodes.has(d.id)).classed("node-dimmed", true);
        labels.filter(d => !edgeConnectedNodes.has(d.id)).classed("label-dimmed", true);
        links.filter(d => 
          !((d.source.id === getSelectedEdge.source && d.target.id === getSelectedEdge.target) ||
            (d.source.id === getSelectedEdge.target && d.target.id === getSelectedEdge.source))
        ).classed("link-dimmed", true);
      }
    }
  }, [selectedNode, connectedNodes, getSelectedEdge, edgeConnectedNodes, isLoading]);

  // Handle URL parameters for focusing on a specific node
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const focusNodeId = urlParams.get('focus');
    
    if (focusNodeId && !isLoading && networkData.nodes) {
      const focusNode = networkData.nodes.find(node => node.id === focusNodeId);
      if (focusNode) {
        setSelectedNode(focusNode);
        // Center on the focused node
        setTimeout(() => {
          centerNetwork();
        }, 200);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, networkData.nodes, centerNetwork]);

  // Expose network state to global window object for share functionality
  useEffect(() => {
    window.networkState = {
      filters,
      zoomLevel,
      selectedNode: selectedNode?.id || null
    };
  }, [filters, zoomLevel, selectedNode]);

  const toggleFilter = useCallback((filterType) => {
    const startTime = performance.now();
    
    setFilters(prev => {
      const newFilters = { ...prev, [filterType]: !prev[filterType] };
      
      // Cache the new filter state
      cacheManager.setSession('filters', 'current', newFilters);
      
      const duration = performance.now() - startTime;
      performanceMonitor.trackUserInteraction('filter-toggle', duration);
      
      return newFilters;
    });
  }, []);

  // Search functionality with caching
  const handleSearch = useCallback((query) => {
    const startTime = performance.now();
    
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    // Check cache first
    const cachedResults = cacheManager.getSearchResults(query);
    if (cachedResults) {
      setSearchResults(cachedResults);
      const duration = performance.now() - startTime;
      performanceMonitor.trackUserInteraction('search-cached', duration);
      return;
    }
    
    const queryLower = query.toLowerCase().trim();
    const queryWords = queryLower.split(/\s+/).filter(word => word.length > 0);
    
    const results = networkData.nodes.filter(node => {
      const nodeId = node.id.toLowerCase();
      const nodeName = node.name.toLowerCase();
      const nodeType = typeMap[node.type] ? typeMap[node.type].toLowerCase() : '';
      
      // For very short queries (1-2 chars), be very restrictive
      if (queryLower.length <= 2) {
        // Only exact matches or starts with for very short queries
        if (nodeId === queryLower || nodeName === queryLower) return true;
        if (nodeId.startsWith(queryLower) || nodeName.startsWith(queryLower)) return true;
        return false;
      }
      
      // For short queries (3 chars), be more restrictive
      if (queryLower.length === 3) {
        // Primary search fields only
        if (nodeId === queryLower || nodeName === queryLower) return true;
        if (nodeId.startsWith(queryLower) || nodeName.startsWith(queryLower)) return true;
        if (nodeId.includes(queryLower) || nodeName.includes(queryLower)) return true;
        
        // Type search for short queries
        if (nodeType.includes(queryLower)) return true;
        return false;
      }
      
      // For multi-word queries, check if all words match
      if (queryWords.length > 1) {
        const allWordsMatch = queryWords.every(word => {
          return nodeId.includes(word) || 
                 nodeName.includes(word) || 
                 nodeType.includes(word) ||
                 (node.website && node.website.toLowerCase().includes(word));
        });
        if (allWordsMatch) return true;
      }
      
      // Primary search fields (high relevance)
      if (nodeId === queryLower || nodeName === queryLower) return true;
      if (nodeId.startsWith(queryLower) || nodeName.startsWith(queryLower)) return true;
      if (nodeId.includes(queryLower) || nodeName.includes(queryLower)) return true;
      
      // Type search (medium relevance) - increased scoring
      if (nodeType.includes(queryLower)) return true;
      
      // Website search (medium relevance) - only if it's a clear match
      if (node.website && node.website.toLowerCase().includes(queryLower)) {
        const websiteLower = node.website.toLowerCase();
        if (websiteLower.includes(queryLower) && 
            (websiteLower.includes(nodeId) || websiteLower.includes(nodeName.split(' ')[0]))) {
          return true;
        }
      }
      
      // Key personnel search (medium relevance) - only for longer queries
      if (queryLower.length >= 4 && node.keyPersonnel) {
        for (const person of node.keyPersonnel) {
          const personName = typeof person === 'string' ? person : person.name;
          if (personName.toLowerCase().includes(queryLower)) return true;
        }
      }
      
      // Description/news search (low relevance) - only for longer queries and specific patterns
      if (queryLower.length >= 4) {
        // Only search description if query appears in first sentence or with organization name
        if (node.description) {
          const descriptionLower = node.description.toLowerCase();
          const firstSentence = descriptionLower.split('.')[0];
          
          if (firstSentence.includes(queryLower) && 
              (firstSentence.includes(nodeId) || firstSentence.includes(nodeName.split(' ')[0]))) {
            return true;
          }
        }
        
        // Only search news if query appears with organization name
        if (node.recentNews) {
          const newsLower = node.recentNews.toLowerCase();
          if (newsLower.includes(queryLower) && 
              (newsLower.includes(nodeId) || newsLower.includes(nodeName.split(' ')[0]))) {
            return true;
          }
        }
      }
      
      return false;
    });
    
    // Enhanced relevance scoring with improved type scoring
    const scoredResults = results.map(node => {
      const nodeId = node.id.toLowerCase();
      const nodeName = node.name.toLowerCase();
      const nodeType = typeMap[node.type] ? typeMap[node.type].toLowerCase() : '';
      let score = 0;
      
      // Exact matches get highest score
      if (nodeId === queryLower) score += 1000;
      if (nodeName === queryLower) score += 1000;
      
      // Starts with matches get high score
      if (nodeId.startsWith(queryLower)) score += 500;
      if (nodeName.startsWith(queryLower)) score += 500;
      
      // Contains matches get medium score
      if (nodeId.includes(queryLower)) score += 100;
      if (nodeName.includes(queryLower)) score += 100;
      
      // Type matches get higher score (increased from 50 to 200)
      if (nodeType.includes(queryLower)) score += 200;
      
      // Multi-word query bonus
      if (queryWords.length > 1) {
        const matchingWords = queryWords.filter(word => 
          nodeId.includes(word) || nodeName.includes(word) || nodeType.includes(word)
        );
        if (matchingWords.length === queryWords.length) {
          score += 300; // Bonus for matching all words
        }
      }
      
      // Website matches get lower score
      if (node.website && node.website.toLowerCase().includes(queryLower)) score += 30;
      
      // Personnel matches get lower score
      if (node.keyPersonnel) {
        for (const person of node.keyPersonnel) {
          const personName = typeof person === 'string' ? person : person.name;
          if (personName.toLowerCase().includes(queryLower)) {
            score += 20;
            break;
          }
        }
      }
      
      // Description/news matches get lowest score
      if (node.description && node.description.toLowerCase().includes(queryLower)) score += 10;
      if (node.recentNews && node.recentNews.toLowerCase().includes(queryLower)) score += 10;
      
      return { ...node, relevanceScore: score };
    });
    
    // Sort by relevance score, then alphabetically
    const sortedResults = scoredResults
      .sort((a, b) => {
        if (b.relevanceScore !== a.relevanceScore) {
          return b.relevanceScore - a.relevanceScore;
        }
        return a.name.localeCompare(b.name);
      })
      .map(({ relevanceScore, ...node }) => node); // Remove score from final results
    
    // Cache results
    cacheManager.cacheSearchResults(query, sortedResults);
    setSearchResults(sortedResults);
    
    const duration = performance.now() - startTime;
    performanceMonitor.trackUserInteraction('search-new', duration);
  }, [networkData.nodes, typeMap]);

  const handleEdgeClick = useCallback((edge, event) => {
    console.log('=== EDGE CLICK DEBUG ===');
    console.log('Edge clicked:', edge.source.id, '->', edge.target.id);
    console.log('Edge object:', edge);
    console.log('Current selected edge ID:', selectedEdgeId);
    console.log('Event target:', event?.target);
    console.log('Event type:', event?.type);
    
    // Stop event propagation to prevent container click handler from firing
    event?.stopPropagation();
    
    // Call the onEdgeClick callback if provided (for NetworkOnly page)
    if (onEdgeClick) {
      onEdgeClick(edge);
      return; // Return early to let the parent component handle the edge click
    }
    
    // Clear node selection when clicking an edge
    setSelectedNode(null);
    selectedNodeRef.current = null;
    setConnectedNodes(new Set());
    
    // Create a unique edge identifier (sorted to handle both directions)
    const createEdgeId = (edge) => {
      const sourceId = edge.source.id;
      const targetId = edge.target.id;
      return sourceId < targetId ? `${sourceId}-${targetId}` : `${targetId}-${sourceId}`;
    };
    
    const clickedEdgeId = createEdgeId(edge);
    
    console.log('Clicked edge ID:', clickedEdgeId);
    console.log('Selected edge ID:', selectedEdgeId);
    console.log('Is same edge?', clickedEdgeId === selectedEdgeId);
    console.log('String comparison details:');
    console.log('  clickedEdgeId type:', typeof clickedEdgeId);
    console.log('  selectedEdgeId type:', typeof selectedEdgeId);
    console.log('  clickedEdgeId length:', clickedEdgeId?.length);
    console.log('  selectedEdgeId length:', selectedEdgeId?.length);
    console.log('  clickedEdgeId === selectedEdgeId:', clickedEdgeId === selectedEdgeId);
    console.log('  clickedEdgeId == selectedEdgeId:', clickedEdgeId === selectedEdgeId);
    console.log('=== END DEBUG ===');
    
    // Toggle selection: if clicking the same edge, deselect it
    setSelectedEdgeId(prevSelectedEdgeId => {
      console.log('Previous selected edge ID:', prevSelectedEdgeId);
      console.log('Clicked edge ID:', clickedEdgeId);
      console.log('Are they the same?', clickedEdgeId === prevSelectedEdgeId);
      
      if (clickedEdgeId === prevSelectedEdgeId) {
        console.log('Deselecting edge:', edge.source.id, '->', edge.target.id);
        console.log('Setting selectedEdgeId to null');
        setEdgeConnectedNodes(new Set());
        setSearchQuery('');
        setSearchResults([]);
        setShowSearch(false);
        // Reset to full network view when deselecting edge
        setTimeout(() => {
          manualCenterNetwork();
        }, 100);
        return null;
      } else {
        console.log('Setting selectedEdgeId to:', clickedEdgeId);
        // Find connected nodes for this edge
        const connected = new Set();
        connected.add(edge.source.id);
        connected.add(edge.target.id);
        
        setEdgeConnectedNodes(connected);
        setSearchQuery('');
        setSearchResults([]);
        setShowSearch(true); // Auto-open the search & details dropdown
        
        // Zoom to show the connected nodes
        setTimeout(() => {
          // Create a virtual node at the center of the edge for zooming
          const sourceNode = simulationRef.current?.nodes().find(n => n.id === edge.source.id);
          const targetNode = simulationRef.current?.nodes().find(n => n.id === edge.target.id);
          
          if (sourceNode && targetNode) {
            // Create a virtual node at the midpoint of the edge
            const virtualNode = {
              id: 'edge-center',
              x: (sourceNode.x + targetNode.x) / 2,
              y: (sourceNode.y + targetNode.y) / 2,
              name: 'Connection',
              type: 'connection',
              sourceId: edge.source.id,
              targetId: edge.target.id
            };
            
            // Zoom to show both connected nodes
            zoomToSubnetwork(virtualNode);
          } else {
            console.log('Could not find source or target nodes for edge zoom');
          }
        }, 100); // Small delay to ensure state updates
        
        // Auto-scroll to the search & details section (only when sidebar is visible)
        if (!hideUI) {
          setTimeout(() => {
            const sidebar = document.querySelector('.network-sidebar-left');
            const searchDropdownButton = document.querySelector('.dropdown-container:last-child .dropdown-button');
            if (sidebar && searchDropdownButton) {
              const scrollTop = searchDropdownButton.offsetTop - sidebar.offsetTop - 5; // 5px gap from top
              sidebar.scrollTo({
                top: scrollTop,
                behavior: 'smooth'
              });
            }
          }, 100); // Small delay to ensure the dropdown is rendered
        }
        
        return clickedEdgeId;
      }
    });
  }, [selectedEdgeId, setSelectedEdgeId, setEdgeConnectedNodes, setSearchQuery, setSearchResults, setShowSearch, manualCenterNetwork, zoomToSubnetwork, hideUI, onEdgeClick]);

  const handleNodeClick = useCallback((node, event) => {
    console.log('Node clicked:', node.id, 'Current selected:', selectedNode?.id);
    
    // Stop event propagation to prevent container click handler from firing
    event?.stopPropagation();
    
    // If onNodeClick is provided, let the parent component handle everything
    if (onNodeClick) {
      onNodeClick(node);
      return; // Return early to prevent any internal logic from executing
    }
    
    // Clear edge selection when clicking a node (only for main page behavior)
    setSelectedEdgeId(null);
    setEdgeConnectedNodes(new Set());
    
    // Toggle selection: if clicking the same node, deselect it (main page behavior only)
    if (selectedNodeRef.current && selectedNodeRef.current.id === node.id) {
      console.log('Deselecting node:', node.id);
      setSelectedNode(null);
      selectedNodeRef.current = null;
      setConnectedNodes(new Set());
      setSearchQuery('');
      setSearchResults([]);
      setShowSearch(false);
      
      // Reset to full network view when deselecting
      setTimeout(() => {
        manualCenterNetwork();
      }, 100);
      return;
    }
    
    // On mobile, allow normal interactions but ensure smooth performance
    if (isMobile) {
      // Ensure smooth performance on mobile
      if (svgRef.current) {
        // Use requestAnimationFrame for smoother updates
        requestAnimationFrame(() => {
          // Allow normal zoom/pan interactions
          // The zoom behavior is already set up in the useEffect
        });
      }
    }
    
    // Find connected nodes for dimming logic
    const connected = new Set();
    connected.add(node.id);
    
    // Find all links connected to this node
    networkData.links.forEach(link => {
      if (link.source === node.id || link.target === node.id) {
        connected.add(link.source);
        connected.add(link.target);
      }
    });
    
    setSelectedNode(node);
    selectedNodeRef.current = node;
    setConnectedNodes(connected);
    
    // Set UI state for main page behavior
    setSearchQuery('');
    setSearchResults([]);
    setShowSearch(true); // Auto-open the search & details dropdown
    
    // Auto-scroll to the search & details section (only when sidebar is visible)
    if (!hideUI) {
      setTimeout(() => {
        const sidebar = document.querySelector('.network-sidebar-left');
        const searchDropdownButton = document.querySelector('.dropdown-container:last-child .control-dropdown-button');
        if (sidebar && searchDropdownButton) {
          const scrollTop = searchDropdownButton.offsetTop - sidebar.offsetTop - 5; // 5px gap from top
          sidebar.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });
        }
      }, 100); // Small delay to ensure the dropdown is rendered
    }
    
    // Zoom to the subnetwork after a short delay to ensure state updates and simulation has settled
    setTimeout(() => {
      zoomToSubnetwork(node);
    }, 100);
  }, [isMobile, selectedNode, networkData.links, zoomToSubnetwork, manualCenterNetwork, hideUI, onNodeClick]);

  return (
    <div className={`network-visualization ${hideUI ? 'hide-ui' : ''}`}>
      {/* Title and Description - Only show when not hiding UI */}
      {!hideUI && (
        <div className="network-header">
          <h2>{dataFile === 'tech' ? 'Atlanta Tech Network' : 'Atlanta Biotech Network'}</h2>
          <p>
            An interactive visualization of the organizations powering innovation in Atlanta {dataFile === 'tech' ? 'tech' : 'biotech'} — including {dataFile === 'tech' ? 'startups, VCs, and innovation hubs' : 'startups, academic institutions, VCs, incubators, and service providers'}. Use the filters to focus on 
            specific organization types. Click on nodes and connections for detailed information and website links.
          </p>
          <div className="header-spacer"></div>
        </div>
      )}

      {/* Main Layout with Sidebars */}
      <div className="network-layout">
        
        {/* Left Sidebar - Controls - Only show when not hiding UI */}
        {!hideUI && (
          <div className="network-sidebar-left">
          {/* Controls Section */}
          <div className="dropdown-container">
            <button className={`dropdown-button ${showControls ? 'expanded' : ''}`} onClick={() => setShowControls(!showControls)}>
              <span>Controls</span>
              <svg className={`dropdown-arrow ${showControls ? 'expanded' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6,9 12,15 18,9"></polyline>
              </svg>
            </button>
            {showControls && (
              <div className="dropdown-content control-dropdown expanded">
                {/* All Controls in One Row */}
                <div className="control-group">
                  <button 
                    className="control-button center-button"
                    onClick={manualCenterNetwork}
                    title="Center Network"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="3" y1="3" x2="8" y2="3"/>
                      <line x1="3" y1="3" x2="3" y2="8"/>
                      <line x1="21" y1="3" x2="16" y2="3"/>
                      <line x1="21" y1="3" x2="21" y2="8"/>
                      <line x1="3" y1="21" x2="8" y2="21"/>
                      <line x1="3" y1="21" x2="3" y2="16"/>
                      <line x1="21" y1="21" x2="21" y2="16"/>
                      <line x1="21" y1="21" x2="16" y2="21"/>
                      <line x1="21" y1="21" x2="21" y2="16"/>
                    </svg>
                  </button>
                  
                  <div className="control-spacer"></div>
                  
                  <button 
                    className="control-button zoom-button"
                    onClick={zoomIn}
                    disabled={zoomLevel >= 1.0}
                    title="Zoom In"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </button>
                  
                                <span className="zoom-level-display">
                {Math.round(zoomLevel * 100)}%
              </span>
                  
                  <button 
                    className="control-button zoom-button"
                    onClick={zoomOut}
                    disabled={zoomLevel <= 0.1}
                    title="Zoom Out"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Filters Section */}
          <div className="dropdown-container">
            <button className={`dropdown-button ${showFilters ? 'expanded' : ''}`} onClick={() => setShowFilters(!showFilters)}>
              <span>Filters</span>
              <svg className={`dropdown-arrow ${showFilters ? 'expanded' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6,9 12,15 18,9"></polyline>
              </svg>
            </button>
            {showFilters && (
              <div className="dropdown-content filter-dropdown expanded">

              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.companies}
                  onChange={() => toggleFilter('companies')}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Companies</span>
              </label>
              
              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.startups}
                  onChange={() => toggleFilter('startups')}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Startups</span>
              </label>
              
              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.universities}
                  onChange={() => toggleFilter('universities')}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Universities</span>
              </label>
              
              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.incubators}
                  onChange={() => toggleFilter('incubators')}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Incubators</span>
              </label>
              
              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.vcs}
                  onChange={() => toggleFilter('vcs')}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Venture Capital</span>
              </label>
              
              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.serviceProviders}
                  onChange={() => toggleFilter('serviceProviders')}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Service Providers</span>
              </label>
              
              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.healthSystems}
                  onChange={() => toggleFilter('healthSystems')}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Health Systems</span>
              </label>
              
              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.government}
                  onChange={() => toggleFilter('government')}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Government</span>
              </label>
              
              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.trade}
                  onChange={() => toggleFilter('trade')}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Trade Organizations</span>
              </label>
              
              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.development}
                  onChange={() => toggleFilter('development')}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Development</span>
              </label>
              
              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.facilities}
                  onChange={() => toggleFilter('facilities')}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Facilities</span>
              </label>
              
              <label className="filter-checkbox">
                <input 
                  type="checkbox" 
                  checked={filters.communityBuilders}
                  onChange={() => toggleFilter('communityBuilders')}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">Community Builders</span>
              </label>
            </div>
            )}
          </div>
          
          {/* Legend Section */}
          <div className="dropdown-container">
            <button className={`dropdown-button ${showLegend ? 'expanded' : ''}`} onClick={() => setShowLegend(!showLegend)}>
              <span>Legend</span>
              <svg className={`dropdown-arrow ${showLegend ? 'expanded' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6,9 12,15 18,9"></polyline>
              </svg>
            </button>
            {showLegend && (
              <div className="dropdown-content legend-dropdown expanded">

                <div className="legend-section">
                  <h5>Node Types</h5>
                  <div className="legend-item">
                    <div className="legend-color" style={{backgroundColor: getNodeColor('university')}}></div>
                    <span className="legend-label">Universities</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color" style={{backgroundColor: getNodeColor('company')}}></div>
                    <span className="legend-label">Companies</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color" style={{backgroundColor: getNodeColor('startup')}}></div>
                    <span className="legend-label">Startups</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color" style={{backgroundColor: getNodeColor('vc')}}></div>
                    <span className="legend-label">Venture Capital</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color" style={{backgroundColor: getNodeColor('incubator')}}></div>
                    <span className="legend-label">Incubators</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color" style={{backgroundColor: getNodeColor('serviceProvider')}}></div>
                    <span className="legend-label">Service Providers</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color" style={{backgroundColor: getNodeColor('health_system')}}></div>
                    <span className="legend-label">Health Systems</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color" style={{backgroundColor: getNodeColor('government')}}></div>
                    <span className="legend-label">Government</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color" style={{backgroundColor: getNodeColor('facility')}}></div>
                    <span className="legend-label">Facilities</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color" style={{backgroundColor: getNodeColor('community')}}></div>
                    <span className="legend-label">Community Builders</span>
                  </div>
                </div>
                
                <div className="legend-section">
                  <h5>Relationship Types</h5>
                  <div className="legend-item">
                    <div className="legend-line" style={{borderColor: '#ff6b6b', borderWidth: '4px'}}></div>
                    <span className="legend-label">Spinout</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-line" style={{borderColor: '#4ecdc4', borderStyle: 'dashed'}}></div>
                    <span className="legend-label">Investment</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-line" style={{borderColor: '#45b7d1'}}></div>
                    <span className="legend-label">Collaboration</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-line" style={{borderColor: '#96ceb4', borderStyle: 'dotted'}}></div>
                    <span className="legend-label">Partnership</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-line" style={{borderColor: '#ffeaa7', borderWidth: '1px'}}></div>
                    <span className="legend-label">Service</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-line" style={{borderColor: '#dda0dd', borderStyle: 'dashed'}}></div>
                    <span className="legend-label">Support</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-line" style={{borderColor: '#9b59b6', borderStyle: 'dashed'}}></div>
                    <span className="legend-label">Education Program</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Search & Details Section */}
          <div className="dropdown-container">
            <button className={`dropdown-button ${showSearch ? 'expanded' : ''}`} onClick={() => setShowSearch(!showSearch)}>
              <span>Search & Details</span>
              <svg className={`dropdown-arrow ${showSearch ? 'expanded' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6,9 12,15 18,9"></polyline>
              </svg>
            </button>
            {showSearch && (
              <div className="dropdown-content search-dropdown expanded">
                {/* Search Input */}
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search companies, universities..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="search-input"
                  />
                </div>
                
                {/* Search Results */}
                {searchQuery.trim() && (
                  <div className="search-results">
                    {searchResults.length > 0 ? (
                      <>
                        <h5>Search Results</h5>
                        <div className="search-results-list">
                          {searchResults.map((node, index) => {
                            const isHidden = exploreMode && exploreState && !exploreState.selectedNodes.has(node.id) && !exploreState.visibleNodes.has(node.id);
                            const isSelected = exploreMode && exploreState && exploreState.selectedNodes.has(node.id);
                            const isVisible = exploreMode && exploreState && exploreState.visibleNodes.has(node.id);
                            
                            return (
                              <div 
                                key={node.id} 
                                className={`search-result-item ${isHidden ? 'search-result-hidden' : ''} ${isSelected ? 'search-result-selected' : ''} ${isVisible ? 'search-result-visible' : ''}`}
                                onClick={() => {
                                  if (exploreMode && isHidden) {
                                    // For hidden nodes in explore mode, trigger the node toggle to show the popup
                                    if (onNodeToggle) {
                                      onNodeToggle(node.id, { stopPropagation: () => {} });
                                    }
                                  } else {
                                    handleNodeClick(node);
                                  }
                                }}
                              >
                                <div className="result-node-color" style={{backgroundColor: getNodeColor(node.type)}}></div>
                                <div className="result-content">
                                  <div className="result-name">{node.name}</div>
                                  <div className="result-type">{typeMap[node.type] || node.type}</div>
                                  {exploreMode && isHidden && (
                                    <div className="result-status">Hidden - Click to show options</div>
                                  )}
                                  {exploreMode && isSelected && (
                                    <div className="result-status">Saved</div>
                                  )}
                                  {exploreMode && isVisible && !isSelected && (
                                    <div className="result-status">Visible</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="no-results">
                        <p>No organizations found matching "{searchQuery}"</p>
                        <p className="no-results-hint">Try a different search term or check the spelling</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Selected Node Details */}
                {selectedNode && (
                  <div className="node-details">
                    <div className="details-header">
                      <h3>{selectedNode.name}</h3>
                      <button 
                        className="details-close"
                        onClick={() => {
                          setSelectedNode(null);
                          selectedNodeRef.current = null; // Clear the ref as well
                          setConnectedNodes(new Set()); // Clear connected nodes
                          // Clear highlighting
                          if (containerRef.current) {
                            const svg = d3.select(containerRef.current).select("svg");
                            svg.selectAll(".node").classed("node-highlighted", false);
                            svg.selectAll(".label").classed("label-highlighted", false);
                          }
                        }}
                        title="Close"
                      >
                        ×
                      </button>
                    </div>
                    
                    <div className="details-content">
                      <p className="node-type">{typeMap[selectedNode.type] || selectedNode.type}</p>
                      <p className="node-description">{selectedNode.description}</p>
                      
                      {selectedNode.website && (
                        <div className="details-section">
                          <h4>Website</h4>
                          <a 
                            href={selectedNode.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="website-link"
                          >
                            {selectedNode.website.replace(/^https?:\/\//, '')}
                          </a>
                        </div>
                      )}
                      
                      {selectedNode.keyPersonnel && selectedNode.keyPersonnel.length > 0 && (
                        <div className="details-section">
                          <h4>Key Personnel</h4>
                          <ul className="personnel-list">
                            {selectedNode.keyPersonnel.map((person, index) => (
                              <li key={index}>
                                {typeof person === 'string' ? person : person.name}
                                {typeof person === 'object' && person.linkedin && (
                                  <a 
                                    href={person.linkedin} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="linkedin-link"
                                  >
                                    LinkedIn
                                  </a>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {selectedNode.recentNews && (
                        <div className="details-section">
                          <h4>Recent Developments</h4>
                          <p className="recent-news">{selectedNode.recentNews}</p>
                        </div>
                      )}
                      
                      {/* Connections List */}
                      <div className="details-section">
                        <h4>Connections</h4>
                        {(() => {
                          // Find all connections for the selected node
                          const nodeConnections = networkData.links.filter(link => 
                            link.source === selectedNode.id || link.target === selectedNode.id
                          );
                          
                          if (nodeConnections.length === 0) {
                            return <p className="no-connections">No connections found.</p>;
                          }
                          
                          return (
                            <div className="connections-list">
                              {nodeConnections.map((connection, index) => {
                                // Determine the connected node (not the selected one)
                                const connectedNodeId = connection.source === selectedNode.id ? connection.target : connection.source;
                                const connectedNode = networkData.nodes.find(n => n.id === connectedNodeId);
                                
                                if (!connectedNode) return null;
                                
                                return (
                                                                      <div 
                                      key={index} 
                                      className="connection-item"
                                      onClick={() => {
                                        // Highlight the connection/edge instead of zooming to the connected node
                                        const edgeToHighlight = {
                                          source: { id: connection.source },
                                          target: { id: connection.target },
                                          type: connection.type,
                                          description: connection.description
                                        };
                                        
                                        // Trigger edge highlighting
                                        handleEdgeClick(edgeToHighlight, { stopPropagation: () => {} });
                                      }}
                                      style={{ cursor: 'pointer' }}
                                      title="Click to highlight this connection"
                                    >
                                    <div className="connection-header">
                                      <div className="connection-node-color" style={{backgroundColor: getNodeColor(connectedNode.type)}}></div>
                                      <div className="connection-content">
                                        <div className="connection-name">
                                          {connectedNode.name}
                                          {connectedNode.website && (
                                            <a 
                                              href={connectedNode.website} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="connection-website-link"
                                              onClick={(e) => e.stopPropagation()} // Prevent triggering connection highlight when clicking website link
                                            >
                                              ↗
                                            </a>
                                          )}
                                        </div>
                                        <div className="connection-type">{typeMap[connectedNode.type] || connectedNode.type}</div>
                                      </div>
                                    </div>
                                    <div className="connection-relationship">
                                      <span className="relationship-label">Relationship:</span>
                                      <span className="relationship-type">
                                        {connection.type.replaceAll('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                      </span>
                                    </div>
                                    {connection.description && (
                                      <div className="connection-description">
                                        {connection.description}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Selected Edge Details */}
                {getSelectedEdge && (
                  <div className="edge-details">
                    <div className="details-header">
                      <h3>Connection Details</h3>
                      <button 
                        className="details-close"
                        onClick={() => {
                          setSelectedEdgeId(null);
                          setEdgeConnectedNodes(new Set());
                          // Clear highlighting
                          if (containerRef.current) {
                            const svg = d3.select(containerRef.current).select("svg");
                            svg.selectAll(".link").classed("link-highlighted", false);
                            svg.selectAll(".node").classed("node-highlighted", false);
                            svg.selectAll(".label").classed("label-highlighted", false);
                          }
                        }}
                        title="Close"
                      >
                        ×
                      </button>
                    </div>
                    
                    <div className="details-content">
                      <div className="details-section">
                        <h4>Relationship Type</h4>
                        <p className="relationship-type">
                          {getSelectedEdge.type.replaceAll('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                      </div>
                      
                      {getSelectedEdge.description && (
                        <div className="details-section">
                          <h4>Description</h4>
                          <p className="relationship-description">{getSelectedEdge.description}</p>
                        </div>
                      )}
                      
                      <div className="details-section">
                        <h4>Connected Organizations</h4>
                        <div className="connected-organizations">
                          <div 
                            className="org-item"
                            onClick={() => {
                              const sourceNode = networkData.nodes.find(n => n.id === getSelectedEdge.source);
                              if (sourceNode) {
                                handleNodeClick(sourceNode, { stopPropagation: () => {} });
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                            title="Click to highlight this organization"
                          >
                            {(() => {
                              const sourceNode = networkData.nodes.find(n => n.id === getSelectedEdge.source);
                              return (
                                <>
                                  <div className="org-node-color" style={{backgroundColor: getNodeColor(sourceNode?.type)}}></div>
                                  <div className="org-content">
                                    <div className="org-name">
                                      {sourceNode?.name || getSelectedEdge.source}
                                      {sourceNode?.website && (
                                        <a 
                                          href={sourceNode.website} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="org-website-link"
                                          onClick={(e) => e.stopPropagation()} // Prevent triggering node click when clicking website link
                                        >
                                          ↗
                                        </a>
                                      )}
                                    </div>
                                    <div className="org-type">{typeMap[sourceNode?.type] || sourceNode?.type}</div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                          <div 
                            className="org-item"
                            onClick={() => {
                              const targetNode = networkData.nodes.find(n => n.id === getSelectedEdge.target);
                              if (targetNode) {
                                handleNodeClick(targetNode, { stopPropagation: () => {} });
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                            title="Click to highlight this organization"
                          >
                            {(() => {
                              const targetNode = networkData.nodes.find(n => n.id === getSelectedEdge.target);
                              return (
                                <>
                                  <div className="org-node-color" style={{backgroundColor: getNodeColor(targetNode?.type)}}></div>
                                  <div className="org-content">
                                    <div className="org-name">
                                      {targetNode?.name || getSelectedEdge.target}
                                      {targetNode?.website && (
                                        <a 
                                          href={targetNode.website} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="org-website-link"
                                          onClick={(e) => e.stopPropagation()} // Prevent triggering node click when clicking website link
                                        >
                                          ↗
                                        </a>
                                      )}
                                    </div>
                                    <div className="org-type">{typeMap[targetNode?.type] || targetNode?.type}</div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Main Network Canvas */}
        <div className="network-main">
          {/* Loading Overlay */}
          {(isLoading || isThemeChanging) && (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
              <p>Loading network...</p>
            </div>
          )}
          
          {/* Network Canvas */}
          <div 
            className={`network-canvas ${(isLoading || isThemeChanging) ? 'repositioning' : ''}`}
            ref={containerRef}
            onClick={(e) => {
              // Clear selection if clicking on empty space (not on nodes, labels, or links)
              const target = e.target;
              const isNode = target.classList.contains('node') || target.classList.contains('label');
              const isLink = target.tagName === 'line';
              const isControl = target.closest('.network-sidebar-left') || target.closest('.dropdown-container');
              
              // If clicking on empty space (not on interactive elements), clear selection
              if (!isNode && !isLink && !isControl) {
                setSelectedNode(null);
                selectedNodeRef.current = null;
                setConnectedNodes(new Set());
                setSelectedEdgeId(null);
                setEdgeConnectedNodes(new Set());
                setSearchQuery('');
                setSearchResults([]);
                setShowSearch(false);
              }
            }}
          >
            <svg ref={svgRef}></svg>
          </div>
        </div>

      </div>
      
      {/* Stats Display - Only show when not hiding UI */}
      {!hideUI && (
        <div className="stats-display">
          <div className="stats-item">
            <span className="stat-label">Organizations:</span>
            <span className="stat-value">{networkData.nodes.length}</span>
          </div>
          <div className="stats-item">
            <span className="stat-label">Connections:</span>
            <span className="stat-value">{networkData.links.length}</span>
          </div>
          <div className="stats-item">
            <span className="stat-label">Last Update:</span>
            <span className="stat-value">{lastCommitDate}</span>
          </div>
        </div>
      )}



    </div>
  );
});

export default NetworkVisualization; 
